import { Outlet, Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useState } from 'react';
import { useUiStore } from '../store/uiStore';
import { useAuthStore } from '../store/authStore';
import { api } from '../api/client';
import i18n from '../i18n/i18n';

type NavItem = {
  to: string;
  label: string;
  icon: string;
  match: (path: string) => boolean;
};

export function AppLayout() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const locale = useUiStore((s) => s.locale);
  const setLocale = useUiStore((s) => s.setLocale);
  const user = useAuthStore((s) => s.user);
  const refreshToken = useAuthStore((s) => s.refreshToken);
  const clear = useAuthStore((s) => s.clear);
  const canUsers = useAuthStore((s) => s.hasPermission('users:read'));
  const canParkings = useAuthStore((s) => s.hasPermission('parkings:read'));
  const canCustomers = useAuthStore((s) => s.hasPermission('customers:read'));
  const canVehicles = useAuthStore((s) => s.hasPermission('vehicles:read'));
  const canSessions = useAuthStore((s) => s.hasPermission('sessions:read'));
  const canTariffs = useAuthStore((s) => s.hasPermission('tariffs:read'));
  const canBilling = useAuthStore((s) => s.hasPermission('billing:read'));
  const canReservations = useAuthStore((s) => s.hasPermission('reservations:read'));
  const canIncidents = useAuthStore((s) => s.hasPermission('incidents:read'));
  const canMaintenance = useAuthStore((s) => s.hasPermission('maintenance:read'));
  const canEmployees = useAuthStore(
    (s) => s.hasPermission('users:read') || s.hasPermission('maintenance:read'),
  );
  const canBarriers = useAuthStore((s) => s.hasPermission('parkings:read'));
  const canAlpr = useAuthStore(
    (s) => s.hasPermission('sessions:read') || s.hasPermission('api:access'),
  );
  const canMatriculas = useAuthStore(
    (s) => s.hasPermission('sessions:read') || s.hasPermission('api:access'),
  );
  const canReports = useAuthStore((s) => s.hasPermission('reports:read'));
  const canSettings = useAuthStore((s) => s.hasPermission('settings:read'));
  const canVigilancia = useAuthStore(
    (s) => s.hasPermission('settings:read') || s.hasPermission('parkings:read'),
  );

  useEffect(() => {
    void i18n.changeLanguage(locale);
  }, [locale]);

  // Close drawer on route change
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  // Lock body scroll while drawer is open
  useEffect(() => {
    if (!drawerOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [drawerOpen]);

  // Close on Escape
  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDrawerOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drawerOpen]);

  const navItems = useMemo(() => {
    const items: NavItem[] = [
      {
        to: '/dashboard',
        label: t('nav.dashboard'),
        icon: 'bi-grid-1x2',
        match: (path) => path.includes('dashboard') || path === '/',
      },
    ];
    if (canUsers)
      items.push({
        to: '/users',
        label: t('nav.users'),
        icon: 'bi-people',
        match: (p) => p.includes('users'),
      });
    if (canParkings)
      items.push({
        to: '/parkings',
        label: t('nav.parkings'),
        icon: 'bi-building',
        match: (p) => p.includes('parkings'),
      });
    if (canCustomers)
      items.push({
        to: '/customers',
        label: t('nav.customers'),
        icon: 'bi-person-badge',
        match: (p) => p.includes('customers'),
      });
    if (canVehicles)
      items.push({
        to: '/vehicles',
        label: t('nav.vehicles'),
        icon: 'bi-car-front',
        match: (p) => p.includes('vehicles'),
      });
    if (canSessions)
      items.push({
        to: '/sessions',
        label: t('nav.sessions'),
        icon: 'bi-ticket-perforated',
        match: (p) => p.includes('sessions'),
      });
    if (canTariffs)
      items.push({
        to: '/tariffs',
        label: t('nav.tariffs'),
        icon: 'bi-cash-stack',
        match: (p) => p.includes('tariffs'),
      });
    if (canBilling)
      items.push({
        to: '/invoices',
        label: t('nav.invoices'),
        icon: 'bi-receipt',
        match: (p) => p.includes('invoices'),
      });
    if (canReservations)
      items.push({
        to: '/reservations',
        label: t('nav.reservations'),
        icon: 'bi-calendar2-check',
        match: (p) => p.includes('reservations'),
      });
    if (canIncidents)
      items.push({
        to: '/incidents',
        label: t('nav.incidents'),
        icon: 'bi-exclamation-triangle',
        match: (p) => p.includes('incidents'),
      });
    if (canMaintenance)
      items.push({
        to: '/maintenance',
        label: t('nav.maintenance'),
        icon: 'bi-tools',
        match: (p) => p.includes('maintenance'),
      });
    if (canEmployees)
      items.push({
        to: '/employees',
        label: t('nav.employees'),
        icon: 'bi-person-workspace',
        match: (p) => p.includes('employees'),
      });
    if (canBarriers)
      items.push({
        to: '/barriers',
        label: t('nav.barriers'),
        icon: 'bi-door-open',
        match: (p) => p.includes('barriers'),
      });
    if (canAlpr)
      items.push({
        to: '/alpr',
        label: t('nav.alpr'),
        icon: 'bi-camera-video',
        match: (p) => p.includes('alpr'),
      });
    if (canMatriculas)
      items.push({
        to: '/matriculas',
        label: t('nav.matriculas'),
        icon: 'bi-card-heading',
        match: (p) => p.includes('matriculas'),
      });
    if (canVigilancia)
      items.push({
        to: '/vigilancia',
        label: t('nav.vigilancia'),
        icon: 'bi-eye',
        match: (p) => p.includes('vigilancia'),
      });
    if (canReports)
      items.push({
        to: '/reports',
        label: t('nav.reports'),
        icon: 'bi-graph-up',
        match: (p) => p.includes('reports'),
      });
    if (canSettings)
      items.push({
        to: '/settings',
        label: t('nav.settings'),
        icon: 'bi-gear',
        match: (p) => p.includes('settings'),
      });
    return items;
  }, [
    t,
    canUsers,
    canParkings,
    canCustomers,
    canVehicles,
    canSessions,
    canTariffs,
    canBilling,
    canReservations,
    canIncidents,
    canMaintenance,
    canEmployees,
    canBarriers,
    canAlpr,
    canMatriculas,
    canVigilancia,
    canReports,
    canSettings,
  ]);

  const logout = async () => {
    try {
      await api.logout(refreshToken ?? undefined);
    } catch {
      // ignore
    }
    clear();
    navigate('/login');
  };

  const current = navItems.find((i) => i.match(location.pathname));

  return (
    <div className={`app-shell${drawerOpen ? ' is-drawer-open' : ''}`}>
      <div
        className="app-sidebar-backdrop"
        aria-hidden={!drawerOpen}
        onClick={() => setDrawerOpen(false)}
      />

      <aside className="app-sidebar" id="app-sidebar" aria-label="Navegación principal">
        <div className="app-sidebar__brand">
          <div className="d-flex align-items-start justify-content-between gap-2">
            <div className="min-w-0">
              <p className="app-sidebar__brand-title">{t('brand')}</p>
              {user ? <span className="app-sidebar__brand-sub">{user.email}</span> : null}
            </div>
            <button
              type="button"
              className="btn btn-icon btn-sidebar-close d-lg-none"
              aria-label="Cerrar menú"
              onClick={() => setDrawerOpen(false)}
            >
              <i className="bi bi-x-lg" />
            </button>
          </div>
        </div>
        <nav className="app-sidebar__nav">
          {navItems.map((item) => {
            const selected = item.match(location.pathname);
            return (
              <RouterLink
                key={item.to}
                to={item.to}
                className={`app-sidebar__link${selected ? ' is-active' : ''}`}
                onClick={() => setDrawerOpen(false)}
              >
                <i className={`bi ${item.icon}`} />
                <span>{item.label}</span>
              </RouterLink>
            );
          })}
        </nav>
      </aside>

      <div className="app-main">
        <header className="app-toolbar">
          <button
            type="button"
            className="btn btn-icon btn-light d-lg-none"
            aria-label="Abrir menú"
            aria-expanded={drawerOpen}
            aria-controls="app-sidebar"
            onClick={() => setDrawerOpen(true)}
          >
            <i className="bi bi-list fs-4" />
          </button>

          <div className="app-toolbar__title min-w-0 flex-grow-1">
            <h3 className="app-toolbar__heading text-truncate mb-0">
              {current?.label ?? t('nav.dashboard')}
            </h3>
          </div>

          <div className="app-toolbar__search d-none d-xl-block">
            <i className="bi bi-search" />
            <input className="form-control" placeholder="Buscar…" readOnly tabIndex={-1} />
          </div>

          <div className="btn-group" role="group" aria-label="Idioma">
            <button
              type="button"
              className={`btn btn-sm ${locale === 'es' ? 'btn-primary' : 'btn-light'}`}
              onClick={() => setLocale('es')}
            >
              ES
            </button>
            <button
              type="button"
              className={`btn btn-sm ${locale === 'en' ? 'btn-primary' : 'btn-light'}`}
              onClick={() => setLocale('en')}
            >
              EN
            </button>
          </div>

          <button
            type="button"
            className="btn btn-light btn-sm"
            onClick={() => void logout()}
            title={t('nav.logout')}
          >
            <i className="bi bi-box-arrow-right" />
            <span className="d-none d-sm-inline ms-1">{t('nav.logout')}</span>
          </button>
        </header>
        <main className="app-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
